import React from 'react';
import { Box, ButtonBase, Divider, Fade, Stack, SxProps } from '@mui/material';

import {
  ISubmission,
  ISubmissionQuestion,
} from '../../../../store/SubmissionSlice';
import { TP_COLORS } from '../../../../constants';

const gridSize = 45; // px

type TQuestionListContainerProps = {
  submission: ISubmission;
  selectedQuestion?: ISubmissionQuestion;
  onQuestionClicked: (item: ISubmissionQuestion) => void;
};

type TQuestionGridItemData = {
  currentItem: ISubmissionQuestion;
  selectedItem?: ISubmissionQuestion;
};

const questionGridContainer: SxProps = {
  display: 'flex',
  overflow: 'auto',
};

const baseQuestionGridItemStyles: SxProps = {
  padding: '5px',
  minWidth: `${gridSize}px`,
  minHeight: `${gridSize}px`,
  maxWidth: `${gridSize}px`,
  maxHeight: `${gridSize}px`,
  fontWeight: 600,
  fontSize: '14px',
  borderRadius: '5px',
};

const questionGridItemDividerStyles: SxProps = {
  borderColor: 'transparent',
  marginTop: '5px',
  borderWidth: '2px',
};

const QuestionListContainer: React.FC<TQuestionListContainerProps> = ({
  submission,
  selectedQuestion,
  onQuestionClicked,
}) => {
  const submissionQuestions = submission.questions || [];

  const getQuestionGridItemData = (attributes: TQuestionGridItemData) => {
    const { currentItem, selectedItem } = attributes;
    const orderNumber = currentItem.order + 1;
    const uniqueKey = `question-item-${orderNumber}`;

    let dividerColor: React.CSSProperties['borderColor'] = 'transparent';
    let backgroundColor: React.CSSProperties['background'] = '#E2E8F0';
    let color: React.CSSProperties['color'] = TP_COLORS.black;

    if (selectedItem && currentItem.question === selectedItem.question) {
      dividerColor = '#01A79F';
    }

    if (submission.status === 'completed') {
      if (currentItem.status === 'skipped') {
        backgroundColor = '#FFF5CC';
        color = TP_COLORS.black;
      } else if (currentItem.answer && currentItem.answer.isCorrect === true) {
        backgroundColor = '#D3FCD2';
        color = '#22C55E';
      } else if (currentItem.answer && currentItem.answer.isCorrect === false) {
        backgroundColor = '#FFE9D5';
        color = '#FF5630';
      }

      if (currentItem.isOpenEnded && currentItem.openEnded) {
        if (currentItem.openEnded.score) {
          backgroundColor = '#D3FCD2';
          color = '#22C55E';
        } else if (currentItem.openEnded.score === 0) {
          backgroundColor = '#FFE9D5';
          color = '#FF5630';
        }
      }
    } else if (submission.status === 'pending') {
      if (currentItem.status === 'completed') {
        backgroundColor = '#BDC2CB';
      }
    }

    return {
      orderNumber,
      uniqueKey,
      dividerColor,
      bgColor: backgroundColor,
      textColor: color,
    };
  };

  return (
    <Fade in>
      <Box sx={questionGridContainer}>
        {submissionQuestions.map((item) => {
          const {
            orderNumber,
            uniqueKey,
            dividerColor,
            bgColor,
            textColor,
          } = getQuestionGridItemData({
            currentItem: item,
            selectedItem: selectedQuestion,
          });

          const dividerStyles = {
            ...questionGridItemDividerStyles,
            borderColor: dividerColor,
          };

          const gridItemStyles: SxProps = {
            ...baseQuestionGridItemStyles,
            backgroundColor: bgColor,
            color: textColor,
          };

          return (
            <Stack key={uniqueKey} style={{ marginRight: '5px' }}>
              <ButtonBase
                sx={gridItemStyles}
                onClick={() => onQuestionClicked(item)}
              >
                {orderNumber}
              </ButtonBase>
              <Divider sx={dividerStyles} />
            </Stack>
          );
        })}
      </Box>
    </Fade>
  );
};

export default QuestionListContainer;
